<template>
  <b-dropdown
    size="sm"
    variant="primary"
    class="mr-1"
    @show="onDropdownShow"
    @hide="isDropdownOpen = false"
  >
    <template #button-content>
      <span
        class="dropdown-title"
        :class="{ 'has-count': shouldShowCount }"
        :data-count="displayedTemplatesCount"
      >
        Templates
      </span>
    </template>
    <b-dropdown-form>
      <b-list-group flush>
        <b-list-group-item class="d-flex justify-content-between align-items-center">
          <b-form-checkbox
            v-model="isSelectedCurrentTemplate"
            size="sm"
            @change="onSelectCurrentTemplate"
          >
            Images in this template
          </b-form-checkbox>
        </b-list-group-item>

        <b-list-group-item class="d-flex justify-content-between align-items-center">
          <b-form-checkbox
            v-model="isSelectedAllTemplate"
            size="sm"
            @change="onSelectAllTemplates"
          >
            All templates
          </b-form-checkbox>
          <b-form-checkbox
            v-model="showProjectNames"
            size="sm"
            button
            :button-variant="showProjectNames ? 'primary' : 'outline-primary'"
          >
            <span
              v-b-tooltip.hover
              :title="showProjectNames ? 'Show Template Names' :'Show Project Names'"
              style="padding-right: 3px;"
            >
              <i 
                :class="['fas', showProjectNames ? 'fa-file' : 'fa-folder']"
              />
            </span>
          </b-form-checkbox>
        </b-list-group-item>
      </b-list-group>
      <b-list-group
        flush
        class="templates-scroll"
      >
        <b-list-group-item
          v-for="template in templates"
          :key="template.instanceId"
          :disabled="getCurrentInstanceId === template.instanceId"
        >
          <b-form-checkbox
            v-model="template.isSelected"
            :disabled="getCurrentInstanceId === template.instanceId"
            size="sm"
            @change="onSelectTemplate"
          >
            <template v-if="showProjectNames && template.project_name">
              {{ template.project_name }}
            </template>
            <template v-else>
              {{ template.name }}
            </template>
          </b-form-checkbox>
        </b-list-group-item>
      </b-list-group>
    </b-dropdown-form>
    <div 
      v-if="showApplyButton"
      class="d-flex justify-content-end align-items-center w-100 py-2 px-3"
    >
      <b-button
        variant="primary"
        @click="$emit('apply')"
      >
        Apply
      </b-button>
    </div>
  </b-dropdown>
</template>

<script>
import { mapGetters } from 'vuex'
import selectedTemplateForApplyToAll from '../../../../../group/modules/editor-control-popup/selected-template-for-apply-to-all';

export default {
  props: {
    localStorageKey: {
      type: String,
      default: '',
      require: true,
    },
    clearLocalStorage: {
      type: Boolean,
      default: false
    },
    showApplyButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSelectedAllTemplate: true,
      showProjectNames: true,
      templates: [],
      isSelectedCurrentTemplate: false,
      isDropdownOpen: false,
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),

    getCurrentInstanceId () {
      return this.selectedInstance.id
    },

    isApplyToCurrentTemplate() {
      return this.isSelectedCurrentTemplate;
    },

    selectedTemplatesTotal() {
      return this.templates.filter(template => template.isSelected).length;
    },

    isOnlyBaseTemplateSelected() {
      return this.selectedTemplatesTotal === 1;
    },

    displayedTemplatesCount() {
      if (this.isOnlyBaseTemplateSelected) {
        return 0;
      }

      return this.selectedTemplatesTotal - 1;
    },

    shouldShowCount() {
      if (this.selectedTemplatesTotal === 0) {
        return false;
      }

      if (this.isOnlyBaseTemplateSelected) {
        return false;
      }

      return true;
    },
  },
  watch: {
    getCurrentInstanceId() {
      this.initSelectTemplates();
    },
  },
  mounted() {
    this.initSelectTemplates();
  },
  methods: {
    // The list is seeded from window.previews at mount, but after an AJAX page switch the
    // sidebar remounts while previews are still loading - re-sync when opened out of date.
    onDropdownShow() {
      this.isDropdownOpen = true;

      const previews = window.previews || [];
      const inSync = this.templates.length === previews.length
        && this.templates.every((template, i) => previews[i] && template.instanceId === previews[i].id);

      if (!inSync) {
        this.initSelectTemplates();
      }
    },

    initSelectTemplates() {
      if (this.clearLocalStorage) {
        localStorage.setItem(this.localStorageKey, []);  
      }
      const selectedTemplates = selectedTemplateForApplyToAll.getSelectedTempaltes(this.localStorageKey);
      const previews = window.previews || [];

      if (!selectedTemplateForApplyToAll.hasSelectedTemplatesKey(this.localStorageKey)) {
        previews.forEach(preview => {
          selectedTemplateForApplyToAll.addSelectedTemplate(preview.id, this.localStorageKey)
        })
      }

      this.templates = previews.map(({ id, template: { name }, data: { project_name, template_name = null } }) => ({
        instanceId: id,
        name: template_name ?? name,
        project_name, 
        isSelected: this.getCurrentInstanceId === id ? true : selectedTemplateForApplyToAll.hasSelectedTemplates(this.localStorageKey, id),
      }));

      selectedTemplates.forEach((instanceID) => {
        const template = this.templates.find(template => template.instanceId === instanceID);

        if (template) {
          template.isSelected = true;
        }
      });

      this.isSelectedAllTemplate = !this.templates.find(template => !template.isSelected);
    },

    onSelectTemplate() {
      this.isSelectedAllTemplate = !this.templates.find(template => !template.isSelected);
      this.isSelectedCurrentTemplate = false;

      this.templates.forEach(template => {
        if (template.isSelected) {
          selectedTemplateForApplyToAll.addSelectedTemplate(template.instanceId, this.localStorageKey)
        } else {
          selectedTemplateForApplyToAll.removeSelectedTemplate(template.instanceId, this.localStorageKey)
        }
      });
    },

    onSelectAllTemplates() {
      if (this.isSelectedAllTemplate) {
        this.isSelectedCurrentTemplate = false;
      }

      this.templates.forEach(template => {
        if (template.instanceId !== this.getCurrentInstanceId) {
          template.isSelected = this.isSelectedAllTemplate;
        }

        if (template.isSelected) {
          selectedTemplateForApplyToAll.addSelectedTemplate(template.instanceId, this.localStorageKey)
        } else {
          selectedTemplateForApplyToAll.removeSelectedTemplate(template.instanceId, this.localStorageKey)
        }
      });
    },

    onSelectCurrentTemplate() {
      if (this.isSelectedCurrentTemplate) {
        this.isSelectedAllTemplate = false;
      }
    },
  }
}
</script>

<style scoped lang="scss">
.b-dropdown-form {
  width: 418px;
  overflow: auto;
  overflow-wrap: anywhere;
  padding: 0;
}

.templates-scroll {
  max-height: 45vh;
  overflow-y: auto;
}

.dropdown-title {
  position: relative;
  display: inline-block;
  padding-right: 24px;
  padding-top: 2px;

  &.has-count {
    padding-right: 24px;

    &::after {
      content: attr(data-count);
      position: absolute;
      top: 50%;
      right: 2px;
      transform: translateY(-50%);
      width: 18px;
      height: 18px;
      background: white;
      color: black;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: bold;
    }
  }
}

</style>
